import React, { useMemo, useState } from "react";
import { Chart } from "primereact/chart";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { ProductFamily } from "../../types";

interface QtdDistributionChartProps {
  data: ProductFamily[];
  category?: string; // category label shown in the subtitle
  isHomePage?: boolean; // default false if not provided
  loading?: boolean; // show a spinner instead of the empty-state while fetching
  // Canonical family->color resolver shared with the results page so legends match.
  colorForFamily?: (familyName: string) => string;
}

// Each horizontal bar is one metric; each stacked segment is a product family's share.
const BAR_LABELS = ["Category Units", "Spend", "Invoice Units"];

// Distinct palette for the family segments; cycles if families exceed its length.
const FAMILY_COLORS = [
  "#173A63", "#F59E0B", "#10B981", "#3B82F6", "#EF4444",
  "#8B5CF6", "#14B8A6", "#F97316", "#EC4899", "#84CC16",
];

const colorAt = (i: number): string => FAMILY_COLORS[i % FAMILY_COLORS.length];

// Pick black or white label text based on the segment background's luminance.
const textOn = (color: string): string => {
  const hsl = color.match(/hsl\(\s*[\d.]+\s*,\s*[\d.]+%\s*,\s*([\d.]+)%/i);
  if (hsl) return Number(hsl[1]) > 60 ? "#1E293B" : "#FFFFFF";
  const c = color.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1E293B" : "#FFFFFF";
};

const toPercent = (ratio: number | undefined): number => (ratio ?? 0) * 100;

const formatPct = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

interface FamilyPct {
  pf: ProductFamily;
  cu: number;
  spend: number;
  iu: number;
}

const QtdDistributionChart: React.FC<QtdDistributionChartProps> = ({
  data,
  category,
  isHomePage = false,
  loading = false,
  colorForFamily,
}) => {
  const [showNoQtd, setShowNoQtd] = useState(false);

  const boxShadow = isHomePage ? "none" : "0 4px 12px rgba(0, 0, 0, 0.1)";

  const { purchased, noQtd } = useMemo(() => {
    const withPct: FamilyPct[] = data.map((pf) => ({
      pf,
      cu: toPercent(pf.product_family_to_category_qtd_ratio),
      spend: toPercent(pf.product_family_to_category_spend_qtd_ratio),
      iu: toPercent(pf.product_family_to_category_invoice_units_qtd_ratio),
    }));

    const isPurchased = (x: FamilyPct) => x.cu > 0 || x.spend > 0 || x.iu > 0;
    const purchasedList = withPct.filter(isPurchased).sort((a, b) => b.cu - a.cu);
    const noQtdList = withPct.filter((x) => !isPurchased(x));

    return { purchased: purchasedList, noQtd: noQtdList };
  }, [data]);

  // Every purchased family is rendered as its own named segment; no top-N collapsing.
  const segments = useMemo(() => {
    if (!purchased.length) return [] as { name: string; cu: number; iu: number; spend: number; color: string }[];
    return purchased.map((x, i) => ({
      name: x.pf.product_family_name,
      cu: x.cu,
      iu: x.iu,
      spend: x.spend,
      color: colorForFamily ? colorForFamily(x.pf.product_family_name) : colorAt(i),
    }));
  }, [purchased, colorForFamily]);

  const chartData = useMemo(() => {
    if (!segments.length) return null;
    return {
      labels: BAR_LABELS,
      datasets: segments.map((seg) => ({
        label: seg.name,
        data: [seg.cu, seg.spend, seg.iu],
        backgroundColor: seg.color,
        borderColor: "#FFFFFF",
        borderWidth: 2,
        borderRadius: 2,
        borderSkipped: false,
      })),
    };
  }, [segments]);

  const chartOptions = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      // Render at (at least) retina density so canvas text/edges stay crisp when the
      // container is upscaled; a bare 1x backing store looks blurry on HiDPI screens.
      devicePixelRatio: Math.max(typeof window !== "undefined" ? window.devicePixelRatio : 1, 2),
      maintainAspectRatio: false,
      layout: { padding: { right: 16 } },
      categoryPercentage: 0.7,
      barPercentage: 0.9,
      plugins: {
        title: { display: false },
        legend: {
          position: "top" as const,
          align: "start" as const,
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            usePointStyle: false,
            padding: 14,
            color: "#334155",
            font: { size: 12, weight: "600" },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${formatPct(ctx.parsed.x)}`,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          display: false,
          grid: { display: false, drawBorder: false },
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { color: "#334155", font: { weight: "600" } },
        },
      },
    }),
    []
  );

  // Draws the "31%" label centered inside each stacked segment wide enough to fit it.
  const valueLabelPlugin = useMemo(
    () => ({
      id: "qtdValueLabels",
      afterDatasetsDraw(chart: any) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset: any, di: number) => {
          const meta = chart.getDatasetMeta(di);
          if (meta.hidden) return;
          const bg = dataset.backgroundColor as string;
          meta.data.forEach((bar: any, index: number) => {
            const value = dataset.data[index];
            if (value == null || value <= 0) return;
            const start = bar.base ?? bar.x;
            const width = Math.abs(bar.x - start);
            if (width < 26) return; // too narrow to label legibly
            ctx.save();
            ctx.font = "600 11px 'Inter', 'Segoe UI', sans-serif";
            ctx.fillStyle = textOn(bg);
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(formatPct(value), (bar.x + start) / 2, bar.y);
            ctx.restore();
          });
        });
      },
    }),
    []
  );

  const subtitle =
    data.length === 0
      ? undefined
      : `${category ? category + " · " : ""}${purchased.length} of ${data.length} families purchased · share of each measure`;

  const chartHeight = Math.max(260, 200 + Math.ceil((segments.length || 1) / 2) * 26);

  return (
    <Card
      title="Quarter-to-Date Distribution"
      subTitle={subtitle}
      style={{ minHeight: 320, border: "none", boxShadow: boxShadow }}
    >
      {loading ? (
        <div
          className="qtd-loading flex flex-column align-items-center justify-content-center"
          style={{ height: 200, gap: 14 }}
        >
          {/* Pin the spinner stroke to the brand navy instead of PrimeReact's color-cycling default. */}
          <style>{`.qtd-loading .p-progress-spinner-circle{stroke:#173A63;animation:p-progress-spinner-dash 1.4s ease-in-out infinite}`}</style>
          <ProgressSpinner
            style={{ width: 44, height: 44 }}
            strokeWidth="4"
            fill="transparent"
            animationDuration="1.4s"
          />
          <span style={{ color: "#173A63", fontWeight: 600, fontSize: 14 }}>
            Loading quarter-to-date distribution…
          </span>
        </div>
      ) : data.length === 0 ? (
        <div className="flex align-items-center justify-content-center" style={{ height: 160 }}>
          <span className="text-500">No quarter-to-date distribution available for this category.</span>
        </div>
      ) : purchased.length === 0 ? (
        <div className="flex align-items-center justify-content-center" style={{ height: 120 }}>
          <span className="text-500">No families purchased this quarter.</span>
        </div>
      ) : (
        <div style={{ height: chartHeight, width: "100%" }}>
          {chartData && (
            <Chart
              type="bar"
              data={chartData}
              options={chartOptions}
              plugins={[valueLabelPlugin]}
              style={{ height: "100%", width: "100%" }}
            />
          )}
        </div>
      )}

      {noQtd.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 4 }}>
          <button
            type="button"
            onClick={() => setShowNoQtd((s) => !s)}
            aria-expanded={showNoQtd}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 0,
              background: "transparent",
              border: "none",
              color: "#475569",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            <i className={`pi ${showNoQtd ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ fontSize: 12 }} />
            <span>
              <strong>{noQtd.length}</strong> famil{noQtd.length === 1 ? "y" : "ies"} with <strong>no QTD purchases</strong>
            </span>
          </button>

          {showNoQtd && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 12 }}>
              {noQtd.map((x) => (
                <span
                  key={x.pf.product_family_id}
                  style={{
                    background: "#F1F5F9",
                    color: "#475569",
                    border: "1px solid #E2E8F0",
                    borderRadius: 16,
                    padding: "4px 12px",
                    fontSize: 13,
                  }}
                >
                  {x.pf.product_family_name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default QtdDistributionChart;
